import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import moment from "moment";
import { DadosOcorrenciaCnab240, IDataDetalharIntgBanc, Ocorrencia } from "../../../../../types/api/financeiro/detalhar-integracao-bancaria";
import { useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import TableDadosEEVDandEEVC from "./TableDadosOcorrencia/TableDadosEEVDandEEVC";
import TableDadosEEFI from "./TableDadosOcorrencia/TableDadosEEFI";
import { FilterList } from "@mui/icons-material";
import FiltrosDetalheIntegracao from "./FiltrosDetalheIntegracao";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import { TEstabelecimento } from "../../../../../types/api/financeiro/estabelecimentos";
import TableDadosSafraEdi from "./TableDadosOcorrencia/TableDadosSafraEdi";
import { confirm } from "material-ui-confirm";
import DialogInserirCaixaBanco from "./TableDadosOcorrencia/DialogInserirCaixaBanco";
import PrintIcon from '@mui/icons-material/Print';
import useImprimirArquivo from "../../../../../lib/useImprimirArquivo";
import TableDadosCNAB240 from "./TableDadosOcorrencia/TableDadosCNAB240";

export default function DialogDetalharIntegracao({
    id,
    open,
    situacao,
    handleClose,
    handleResetGrupoIntegracao
}: DialogDetalharIntegracaoProps) {

    const imprimirArquivo = useImprimirArquivo();

    const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([])
    const [filtros, setFiltros] = useState<TFiltrosDetalheIntegracao | null>({
        status_contem: (
            situacao === "Pendente de gravação em caixas e bancos" ||
            situacao === "Finalizado"
        ) ? null : ["Aguardando finalização"],
        resposta_contem: null,
        estabelecimento: null,
        ocorrencias: null
    })
    const [filtrosOpen, setFiltrosOpen] = useState(false)
    const [inserirCaixaBancoOpen, setInserirCaixaBancoOpen] = useState(false);
    const [previewOcorrencia, setPreviewOcorrencia] = useState<TPreviewOcorrencia | null>(null);

    //Querys e Mutations
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["detalhar-integracao-bancaria", id, filtros],
        queryFn: () => {
            return axiosClient.get("/financeiro/integracao-bancaria/detalhar-integracao-bancaria/" + id, {
                params:
                {
                    status_contem: filtros?.status_contem,
                    resposta_contem: filtros?.resposta_contem,
                    estabelecimentos_contem: filtros?.estabelecimento?.map((item) => item.numero_estabelecimento),
                    ocorrencias: filtros?.ocorrencias
                }
            });
        },
        onSuccess: ({ data }) => {
            setOcorrencias(data.ocorrencias);
        },
        cacheTime: 0
    });
    const dados: IDataDetalharIntgBanc = data?.data || null

    // Atualizar filtros quando dados são carregados
    useEffect(() => {
         if (dados?.tipo === 'CNAB240' && ocorrencias.length === 0) {
            setFiltros(prev => prev ? {
                ...prev,
                ocorrencias: ['Liquidação']
            } : null);
        }
    }, [dados?.tipo, ocorrencias.length]);

    const { mutate: finalizarIntegracao, isLoading: loadingMutation } = useMutation({
        mutationKey: "integracao-bancaria/finalizar-integracao-bancaria",
        mutationFn: (input: FormData) => axiosClient.post(
            `financeiro/integracao-bancaria/finalizar-integracao-bancaria/${id}`,
            input
        ),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar("Erro ao finalizar integração", { variant: "error" })
        },
        onSuccess: () => {
            enqueueSnackbar(`Integração finalizado com sucessso`, { variant: "success" })
            handleResetGrupoIntegracao()
            refetch()
        }
    });

    const { mutate: inserirCaixaBanco, isLoading: loadingCaixaBanco } = useMutation({
        mutationKey: "inserir-caixas-bancos-integracao-bancaria",
        mutationFn: (input: TFormInserirCaixaBanco) => axiosClient.post(
            `financeiro/integracao-bancaria/inserir-caixas-bancos-integracao-bancaria/${id}`,
            input
        ),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar("Erro ao finalizar integração", { variant: "error" })
        },
        onSuccess: (data) => {
            if (!previewOcorrencia) {
                setInserirCaixaBancoOpen(true)
                return setPreviewOcorrencia(data.data)
            }
            enqueueSnackbar(`Caixa e bancos inserido com sucesso!`, { variant: "success" })
            handleResetGrupoIntegracao()
            refetch()
        }
    });
    //

    //funcoes manipuladoras
    const handleAlterarLinha = (id: number, titulo?: string, observacao?: string) => {
        const novoArray = ocorrencias.map((item) => {
            if (item.id === id) return { ...item, resumo_dados: { ...item.resumo_dados, titulo: titulo ?? null, observacao: observacao ?? null } }
            return item
        })
        setOcorrencias(novoArray)
    }

    const handleImprimir = () => {
        imprimirArquivo(
            "financeiro/integracao-bancaria/imprimir-relatorio-integracao-bancaria/" +
            id
        );
    };

    const handleFinalizar = useCallback(
        async () => {
            confirm({ description: "Você tem certeza que deseja finalizar essa integração?" })
                .then(() => {
                    if (dados.tipo === 'RedeEEFI') { return finalizarIntegracao({}); }
                    if (dados.tipo === 'Safra EDI') {
                        const inputArray = ocorrencias.filter(item => item.status === "Aguardando finalização" && item.resumo_dados.titulo).map((item) => ({
                            id: item.id,
                            titulo: item.resumo_dados.titulo || "",
                            observacao: item.resumo_dados.titulo === "Resolvido por fora" ? item.resumo_dados.observacao || "" : ""
                        }))
                        return finalizarIntegracao({ ocorrencias: inputArray })
                    }
                    if (dados.tipo === 'CNAB240') {
                        const inputArray = ocorrencias.filter((item) => {
                            if (item.status === "Aguardando finalização") {
                                const resumo = item.resumo_dados as DadosOcorrenciaCnab240;
                                const valorArredondado = Math.round(resumo.valor * 100) / 100;
                                const saldoSimuladoArredondado = Math.round(resumo.titulos_possiveis.reduce((acc, curr) => acc + curr.saldo_simulado, 0) * 100) / 100;
                                return (valorArredondado === saldoSimuladoArredondado && resumo.total_possiveis === resumo.total_cobrancas);
                            }
                            return false;
                        }).map((item) => {
                            const resumo = item.resumo_dados as DadosOcorrenciaCnab240;

                            return {
                                id: item.id,
                                titulos: resumo.titulos_possiveis.map(titulo => titulo.identificacao),
                                observacao: resumo.observacao || ""
                            };
                        });
                        return finalizarIntegracao({ ocorrencias: inputArray })
                    }
                    const inputArray = ocorrencias.map((item) => ({ id: item.id, titulo: item.resumo_dados.titulo || "" }))
                    finalizarIntegracao({ ocorrencias: inputArray })
                })
                .catch(() => { });
        },
        [finalizarIntegracao, dados, ocorrencias]
    );

    const handleInserirCaixaBanco = () => {
        setInserirCaixaBancoOpen(false);
        inserirCaixaBanco({ preview: false })
    }

    //

    //valores importantes
    const somenteVisualizacao = dados?.status === "Finalizado"

    const isSafraEdi = useMemo(() => {
        return dados?.tipo === 'Safra EDI';
    }, [dados])

    const isCnab240 = useMemo(() => {
        return dados?.tipo === 'CNAB240';
    }, [dados]);

    const permitirFinalizar = useMemo(() => {
        if (dados && dados.tipo === 'RedeEEFI') {
            return ocorrencias.some(item => item.status !== "Finalizado" && item.status !== "Conflito encontrado")
        }
        return ocorrencias.some(item => item.status !== "Finalizado" && item.resumo_dados.titulo !== null)
    }, [ocorrencias, dados])
    //

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                fullScreen
                maxWidth="xl"
            >
                <DialogTitle>Integração</DialogTitle>
                <DialogContent>

                    {
                        isLoading &&
                        <Box display="flex" justifyContent="center" my={4}>
                            <CircularProgress />
                        </Box>
                    }

                    {
                        (!isLoading && dados) &&
                        <>
                            <Grid container spacing={5}>
                                <Grid container size={{xs: 9}} spacing={5}>
                                    <Grid>
                                        <Typography variant="caption">Tipo</Typography>
                                        <Typography fontWeight="bold">{dados.tipo}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="caption">Criado em</Typography>
                                        <Typography fontWeight="bold">{moment(dados.created_at).format('DD/MM/YYYY HH:mm')}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="caption">Criado por</Typography>
                                        <Typography fontWeight="bold">{dados.usuario}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="caption">Data arquivo</Typography>
                                        <Typography fontWeight="bold">{moment(dados.data_referencia_arquivo).format('DD/MM/YYYY')}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="caption">Situação</Typography>
                                        <Typography fontWeight="bold">{dados.status}</Typography>
                                    </Grid>
                                    {
                                        isCnab240 &&
                                        <Grid>
                                            <Typography variant="caption">Banco</Typography>
                                            <Typography fontWeight="bold">{dados.banco ? `${dados.banco.codigo} - ${dados.banco.nome}` : ''}</Typography>
                                        </Grid>
                                    }
                                </Grid>
                                <Grid size={{xs: 3}}>
                                    <Stack direction={'row'} justifyContent="flex-end" spacing={2}>
                                        <Button
                                            sx={{ minWidth: 100 }}
                                            variant="outlined"
                                            color="primary"
                                            endIcon={<FilterList />}
                                            onClick={() => setFiltrosOpen(true)}
                                        >
                                            Filtros
                                        </Button>
                                        <Button
                                            sx={{ minWidth: 100 }}
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<PrintIcon />}
                                            onClick={handleImprimir}
                                        >
                                            Imprimir
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3 }} />
                            {
                                dados.tipo !== 'RedeEEFI' && !isSafraEdi && !isCnab240 &&
                                <TableDadosEEVDandEEVC
                                    {...{ handleAlterarLinha, somenteVisualizacao }}
                                    tipo={dados.tipo}
                                    rows={ocorrencias}
                                />
                            }
                            {
                                isSafraEdi && !isCnab240 &&
                                <TableDadosSafraEdi
                                    {...{ handleAlterarLinha, somenteVisualizacao }}
                                    tipo={dados.tipo}
                                    rows={ocorrencias}
                                />
                            }
                            {
                                isCnab240 &&
                               <>
                                    <TableDadosCNAB240
                                        key="cnab240-table"
                                        {...{ handleAlterarLinha, somenteVisualizacao }}
                                        tipo={dados.tipo}
                                        rows={ocorrencias} />
                                </>
                            }
                            {
                                dados.tipo === 'RedeEEFI' &&
                                <TableDadosEEFI
                                    rows={ocorrencias} />
                            }
                        </>
                    }
                </DialogContent>

                <DialogActions>
                    <Box mx={2} width={'100%'} display={'flex'} justifyContent={'space-between'}>
                        <Box display={'flex'} gap={4}>
                            {
                                (!isLoading && dados) &&
                                <>
                                    {
                                        isSafraEdi &&
                                        <>
                                            <Box>
                                                <Typography variant="caption">Total crédito líquido</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_credito_liquido)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption">Total débito líquido</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_debito_liquido)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption">Total líquido</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_debito_liquido + dados.total_credito_liquido)}</Typography>
                                            </Box>
                                        </>
                                    }
                                    {
                                        isCnab240 &&
                                        <>
                                            <Box>
                                                <Typography variant="caption">Total títulos</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_titulos)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption">Total recebidos</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_recebido)}</Typography>
                                            </Box>
                                        </>
                                    }
                                    {!isSafraEdi && !isCnab240 &&
                                        <>
                                            <Box>
                                                <Typography variant="caption">Total líquido</Typography>
                                                <Typography fontWeight={'bold'}>{formatarValorMonetario(dados.total_liquido)}</Typography>
                                            </Box>
                                        </>
                                    }
                                    <Box>
                                        <Typography variant="caption">Qtd. registros</Typography>
                                        <Typography fontWeight={'bold'}>{dados.quantidade_registros}</Typography>
                                    </Box>
                                </>
                            }
                        </Box>
                        <Box display={'flex'} gap={2}>
                            <Button onClick={handleClose} variant="outlined"
                                disabled={loadingMutation || loadingCaixaBanco}>
                                Fechar
                            </Button>

                            {
                                !somenteVisualizacao &&
                                <Button
                                    disabled={!permitirFinalizar || loadingMutation || loadingCaixaBanco}
                                    variant="contained"
                                    size="medium"
                                    onClick={handleFinalizar}
                                >
                                    {loadingMutation && < CircularProgress size={20} sx={{ mr: 1 }} />}
                                    {isSafraEdi ? 'Realizar baixa' : 'Finalizar'}
                                </Button>
                            }
                            {
                                !somenteVisualizacao && isSafraEdi &&
                                <Button
                                    disabled={dados.status !== 'Pendente de gravação em caixas e bancos' || loadingMutation || loadingCaixaBanco}
                                    variant="contained"
                                    onClick={() => {
                                        inserirCaixaBanco({ preview: true })
                                    }}
                                >
                                    {loadingMutation && < CircularProgress size={20} sx={{ mr: 1 }} />}
                                    Inserir Caixas/Bancos
                                </Button>
                            }
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
            {
                filtrosOpen &&
                <FiltrosDetalheIntegracao
                    tipo={dados.tipo}
                    filtros={filtros}
                    onSubmit={(filtros: TFiltrosDetalheIntegracao) => setFiltros(filtros)}
                    open={filtrosOpen}
                    onClose={() => setFiltrosOpen(false)}
                />
            }
            {
                inserirCaixaBancoOpen && previewOcorrencia &&
                <DialogInserirCaixaBanco
                    open={inserirCaixaBancoOpen}
                    onClose={() => setInserirCaixaBancoOpen(false)}
                    preview={previewOcorrencia}
                    onConfirm={handleInserirCaixaBanco}
                />

            }
        </>
    )
}
interface DialogDetalharIntegracaoProps {
    open: boolean;
    situacao: string;
    id: number;
    handleClose: () => void;
    handleResetGrupoIntegracao: () => void;
}

type FormData = {
    ocorrencias?: Array<{
        id: number,
        titulo?: string
        titulos?: string[]
    }>
}

type TFormInserirCaixaBanco = {
    preview: boolean,
}

export type TPreviewOcorrencia = {
    preview: boolean;
    valores_agrupados_por_fonte_cba: Array<{
        fonte_cba: string;
        valor_total: string;
    }>;
    creditos_a_identificar: Array<{
        titulo: string;
        valor: string;
    }>;
    data_arquivo: string;
}

export type TFiltrosDetalheIntegracao = {
    status_contem: string[] | null,
    resposta_contem: string[] | null,
    estabelecimento: TEstabelecimento[] | null,
    ocorrencias: string[] | null
}

